import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchMapList, likeMap, unlikeMap, type MapSummary } from '../../apiClient/maps';
import { useAuth } from '../../auth/AuthProvider';
import {
  chartDisplayBpm,
  chartDurationSeconds,
  formatDuration,
  stripVariantSuffix,
} from '@shared/chart';
import { parseVariantSlug, variantLabel } from '@shared/gameTypes';
import { saveModePref } from '../../game/modePref';
import { modePath } from '../../game/modeRouting';
import '../browse.css';
import '../play.css';

type SortOption = 'likes' | 'recent' | 'difficulty' | 'plays';

/**
 * Community discovery for Guitar UGC. Mirrors the Pulse browse page —
 * search / sort / difficulty filter, like toggles, a "Make your own"
 * CTA, and edit links on maps the signed-in player owns — but pinned to
 * `mode=guitar`. Built-in tracks can be toggled on so the page doubles
 * as a full catalogue view.
 */
export function GuitarBrowsePage() {
  const { user } = useAuth();
  const [maps, setMaps] = useState<MapSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortOption>('likes');
  const [search, setSearch] = useState('');
  const [difficulty, setDifficulty] = useState<number | undefined>(undefined);
  const [includeBuiltin, setIncludeBuiltin] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    saveModePref('guitar');
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchMapList({
        mode: 'guitar',
        sort,
        search: debouncedSearch || undefined,
        difficulty,
        includeBuiltin,
        limit: 60,
      });
      setMaps(res.maps);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load maps');
    } finally {
      setLoading(false);
    }
  }, [sort, debouncedSearch, difficulty, includeBuiltin]);

  useEffect(() => {
    void load();
  }, [load]);

  const onToggleLike = async (m: MapSummary) => {
    const optimistic = !m.liked;
    setMaps((prev) =>
      prev.map((x) =>
        x.id === m.id
          ? { ...x, liked: optimistic, likeCount: x.likeCount + (optimistic ? 1 : -1) }
          : x,
      ),
    );
    try {
      const fn = optimistic ? likeMap : unlikeMap;
      const result = await fn(m.id);
      setMaps((prev) =>
        prev.map((x) => (x.id === m.id ? { ...x, liked: result.liked, likeCount: result.count } : x)),
      );
    } catch (err) {
      console.warn('Like toggle failed:', err);
      setMaps((prev) =>
        prev.map((x) => (x.id === m.id ? { ...x, liked: m.liked, likeCount: m.likeCount } : x)),
      );
      setError(err instanceof Error ? err.message : 'Failed to update like');
    }
  };

  const hasActiveFilters =
    debouncedSearch.length > 0 || difficulty !== undefined || includeBuiltin || sort !== 'likes';

  return (
    <div className="browse-page" data-testid="guitar-browse-page">
      <header className="browse-header">
        <div>
          <h2>Community Guitar maps</h2>
          <p className="muted">{total.toLocaleString()} maps</p>
        </div>
        <div className="builder-header-actions">
          <Link to={modePath('guitar')} className="btn btn-ghost">
            Featured tracks
          </Link>
          <Link to={modePath('guitar', 'build')} className="btn btn-primary" data-testid="guitar-browse-create">
            Make your own
          </Link>
        </div>
      </header>

      <p className="browse-intro muted">
        Free guitar / clone-hero maps with custom songs — community-made charts
        you can play in your browser, no download. Build your own, too.
      </p>

      <div className="browse-controls card">
        <input
          type="text"
          placeholder="Search title or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="browse-search"
          aria-label="Search maps by title or description"
          data-testid="guitar-browse-search"
        />
        <div className="browse-filter-group">
          <label className="muted" htmlFor="guitar-sort-select">
            Sort
          </label>
          <select
            id="guitar-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value as SortOption)}
            data-testid="guitar-browse-sort"
          >
            <option value="likes">Most liked</option>
            <option value="recent">Newest</option>
            <option value="plays">Most played</option>
            <option value="difficulty">Hardest</option>
          </select>
        </div>
        <div className="browse-filter-group">
          <label className="muted" htmlFor="guitar-diff-select">
            Difficulty
          </label>
          <select
            id="guitar-diff-select"
            value={difficulty ?? 'any'}
            onChange={(e) =>
              setDifficulty(e.target.value === 'any' ? undefined : Number(e.target.value))
            }
            data-testid="guitar-browse-difficulty"
          >
            <option value="any">Any</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
          </select>
        </div>
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={includeBuiltin}
            onChange={(e) => setIncludeBuiltin(e.target.checked)}
            data-testid="guitar-browse-builtin"
          />
          <span>Show official tracks</span>
        </label>
      </div>

      {error && <div className="error-box">{error}</div>}
      {loading && maps.length === 0 && (
        <div className="browse-grid" data-testid="guitar-browse-skeleton">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="browse-card browse-card-skeleton">
              <div className="browse-card-thumb" />
              <div className="browse-card-body">
                <div className="skeleton-line skeleton-title" />
                <div className="skeleton-line skeleton-meta" />
                <div className="skeleton-line skeleton-desc" />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="browse-grid">
        {maps.map((m) => {
          // Variant pills only ever apply to builtins — UGC slugs are
          // user-titled and could coincidentally end in `-hard`.
          const variant = m.isBuiltin ? parseVariantSlug(m.slug).variant : null;
          const bareTitle = variant ? stripVariantSuffix({ title: m.title, variant }) : m.title;
          return (
            <article
              key={m.id}
              className="browse-card"
              data-testid={`guitar-browse-card-${m.slug}`}
              style={
                {
                  '--map-bg': m.palette.bg,
                  '--map-bg-accent': m.palette.bgAccent,
                  '--map-pad': m.palette.pad,
                  '--map-player': m.palette.player,
                } as React.CSSProperties
              }
            >
              <Link to={modePath('guitar', `play/${m.slug}`)} className="browse-card-thumb">
                <div className="thumb-bg" />
                <div className="thumb-rail thumb-rail-top" />
                <div className="thumb-rail thumb-rail-bottom" />
                <div className="thumb-player" />
                {m.isBuiltin && <span className="thumb-badge">Official</span>}
              </Link>
              <div className="browse-card-body">
                <div className="browse-card-row">
                  <Link to={modePath('guitar', `play/${m.slug}`)} className="browse-card-title">
                    {bareTitle}
                    {variant && (
                      <>
                        {' '}
                        <span
                          className={`variant-badge variant-badge-sm variant-badge-${variant}`}
                          data-testid={`guitar-browse-variant-${m.slug}`}
                        >
                          {variantLabel(variant).toUpperCase()}
                        </span>
                      </>
                    )}
                  </Link>
                  <button
                    type="button"
                    className={`like-btn ${m.liked ? 'liked' : ''}`}
                    onClick={() => void onToggleLike(m)}
                    aria-label={m.liked ? 'Unlike' : 'Like'}
                    aria-pressed={m.liked}
                    data-testid={`guitar-like-${m.slug}`}
                  >
                    <span className="heart">{m.liked ? '\u2665' : '\u2661'}</span>
                    <span>{m.likeCount.toLocaleString()}</span>
                  </button>
                </div>
                <div className="browse-card-meta">
                  <span>{chartDisplayBpm(m.bpm).toFixed(0)} BPM</span>
                  <span title="Duration">
                    {formatDuration(chartDurationSeconds(m.bpm, m.totalBeats))}
                  </span>
                  <span className="diff-pips">
                    {Array.from({ length: 5 }, (_, i) => (
                      <span key={i} className={i < m.difficulty ? 'pip pip-on' : 'pip'} />
                    ))}
                  </span>
                  <span className="muted">{m.playCount.toLocaleString()} plays</span>
                </div>
                {m.description && <p className="browse-card-desc">{m.description}</p>}
                {!m.isBuiltin && user?.uid === m.authorUid && (
                  <Link
                    to={modePath('guitar', `build/${m.slug}`)}
                    className="muted faint browse-edit-link"
                  >
                    Edit your map
                  </Link>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {!loading && maps.length === 0 && (
        <div className="empty-state">
          {error ? (
            <>
              <p className="muted">Couldn&apos;t load maps right now.</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void load()}
                data-testid="guitar-retry-load"
              >
                Try again
              </button>
            </>
          ) : (
            <>
              <p className="muted">No community Guitar maps match your filters.</p>
              {hasActiveFilters ? (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setSearch('');
                    setDifficulty(undefined);
                    setIncludeBuiltin(false);
                    setSort('likes');
                  }}
                  data-testid="guitar-clear-filters"
                >
                  Clear filters
                </button>
              ) : (
                <Link to={modePath('guitar', 'build')} className="btn">
                  Build the first one
                </Link>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
